import { useEffect, useState } from 'react';

/**
 * Modo dia, modo noite, ou automático conforme o computador.
 *
 * A escolha vive no navegador de quem usa, não no banco: é preferência de
 * aparelho, não de pessoa. O Richard pode querer claro no monitor do escritório
 * e escuro no notebook à noite — e são o mesmo login.
 *
 * O tema é aplicado no `<html>` como `data-tema`, e todo o resto sai de
 * `tokens.css`. Nenhum componente conhece cor: se um dia o escuro precisar de
 * ajuste, o ajuste é num arquivo só.
 */
export type Tema = 'claro' | 'escuro' | 'automatico';

const CHAVE = 'kairos-tema';

function preferenciaDoSistema(): 'claro' | 'escuro' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'escuro' : 'claro';
}

function aplicar(tema: Tema): void {
  const efetivo = tema === 'automatico' ? preferenciaDoSistema() : tema;
  document.documentElement.setAttribute('data-tema', efetivo);
}

/** Aplica antes do React montar, para a tela não piscar branca no escuro. */
export function aplicarTemaSalvo(): void {
  const salvo = (localStorage.getItem(CHAVE) as Tema | null) ?? 'automatico';
  aplicar(salvo);
}

export function usarTema(): [Tema, (t: Tema) => void] {
  const [tema, setTema] = useState<Tema>(
    () => (localStorage.getItem(CHAVE) as Tema | null) ?? 'automatico',
  );

  useEffect(() => {
    aplicar(tema);
    localStorage.setItem(CHAVE, tema);

    // No automático, mudar o tema do computador muda o Hub na hora — sem
    // recarregar, sem a pessoa precisar saber que existe uma configuração.
    if (tema !== 'automatico') return;
    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    const aoMudar = () => aplicar('automatico');
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, [tema]);

  return [tema, setTema];
}
